
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    InputAdornment,
    IconButton,
    Alert,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ContentCopy, VpnKey } from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import { LaserMachine } from '../../types';
import { machineService } from '../../services/api';
import { copyTextToClipboard, promptManualCopy } from '../../utils/clipboard';

interface OfflineLicenseDialogProps {
    open: boolean;
    onClose: () => void;
    machine: LaserMachine | null;
}

const OfflineLicenseDialog: React.FC<OfflineLicenseDialogProps> = ({
    open,
    onClose,
    machine,
}) => {
    const [expirationDate, setExpirationDate] = useState<Dayjs | null>(dayjs().add(30, 'day'));
    const [type, setType] = useState('STANDARD');
    const [generatedCode, setGeneratedCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [copyInfo, setCopyInfo] = useState('');

    useEffect(() => {
        if (open) {
            setGeneratedCode('');
            setError('');
            setCopyInfo('');
            setLoading(false);
            // Reset defaults
            setExpirationDate(dayjs().add(30, 'day'));
            setType('STANDARD');
        }
    }, [open]);

    const handleGenerate = async () => {
        if (!machine) return;

        setLoading(true);
        setError('');
        setGeneratedCode('');

        try {
            const response = await machineService.generateOfflineLicense(machine.id, {
                expirationDate: expirationDate ? expirationDate.toDate() : null, // null means permanent if we supported it UI-wise, but backend handles it
                type,
            });

            if (response && response.data && response.data.code) {
                setGeneratedCode(response.data.code);
            } else {
                setError('Risposta non valida dal server');
            }
        } catch (err: any) {
            console.error(err);
            setError(err.response?.data?.message || 'Errore durante la generazione');
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = async () => {
        if (generatedCode) {
            const copied = await copyTextToClipboard(generatedCode);
            if (copied) {
                setCopyInfo('Codice copiato negli appunti');
                return;
            }
            promptManualCopy(generatedCode, 'il codice licenza');
            setCopyInfo('Clipboard non disponibile: usa copia manuale');
        }
    };

    if (!machine) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VpnKey color="primary" /> Genera Licenza Offline
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        Genera un codice di sblocco per la macchina <strong>{machine.name}</strong> ({machine.serialNumber}).
                        Questo codice deve essere inserito manualmente sulla macchina se offline.
                    </Typography>

                    <TextField
                        label="Numero Seriale"
                        value={machine.serialNumber}
                        fullWidth
                        InputProps={{ readOnly: true }}
                        variant="filled"
                    />

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Data Scadenza"
                            value={expirationDate}
                            onChange={(newValue) => setExpirationDate(newValue)}
                            slotProps={{ textField: { fullWidth: true } }}
                        />
                    </LocalizationProvider>

                    <FormControl fullWidth>
                        <InputLabel>Tipo Licenza</InputLabel>
                        <Select
                            value={type}
                            label="Tipo Licenza"
                            onChange={(e) => setType(e.target.value)}
                        >
                            <MenuItem value="STANDARD">STANDARD</MenuItem>
                            <MenuItem value="PRO">PRO</MenuItem>
                            <MenuItem value="ENTERPRISE">ENTERPRISE</MenuItem>
                        </Select>
                    </FormControl>

                    {error && <Alert severity="error">{error}</Alert>}
                    {copyInfo && <Alert severity="info">{copyInfo}</Alert>}

                    {generatedCode && (
                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                Codice Generato:
                            </Typography>
                            <TextField
                                value={generatedCode}
                                fullWidth
                                multiline
                                rows={4}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={copyToClipboard} edge="end">
                                                <ContentCopy />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Chiudi</Button>
                <Button
                    onClick={handleGenerate}
                    variant="contained"
                    disabled={loading || !!generatedCode}
                >
                    {loading ? 'Generazione...' : 'Genera Codice'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default OfflineLicenseDialog;
